import { Portal as TooltipPortal } from "@radix-ui/react-tooltip";
import { useId } from "react";
import type { BrandKit } from "../models/BrandKit";
import { Check, ImageOff, Images, LoaderCircle } from "lucide-react";
import {
  VISUAL_STYLE_OPTIONS,
  VisualStyleId,
  brandKitImagesProcessing,
  brandKitImagesDisabledReason,
} from "../constants/visualStyles";
import { cn } from "../lib/utils";
import { useFirebaseUrl } from "../hooks/useFirebaseUrl";
import { useLoadedImageUrls } from "../hooks/useLoadedImageUrls";
import { logoSurfaceClass, useLogoTone } from "../lib/logoTone";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

interface VisualStyleSelectorProps {
  brandKit?: BrandKit;
  sourceMayHaveImages?: boolean;
  selectedStyleId: VisualStyleId;
  onChange: (styleId: VisualStyleId) => void;
}

// Each concrete style ships 3 authentic sample renders from the real image
// pipeline. Index 1 is the card thumbnail; all 3 show in the hover preview.
const SAMPLE_COUNT = 3;
const styleSample = (styleId: VisualStyleId, n: number) =>
  `/visual-styles/${styleId}-${n}.webp`;

// Auto isn't a look — it picks the best fit per post. Its previews are composed
// from the concrete styles it chooses among, so it reads as a mix, not a style.
const AUTO_MIX: VisualStyleId[] = [
  "photo",
  "cinematic",
  "clean-illustration",
  "modern-graphic",
];

function SampleTile({ src }: { src: string }) {
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      draggable={false}
      className="h-full w-full object-cover"
    />
  );
}

type ImagesIconState = "available" | "empty" | "processing";

// The icon itself carries the disabled state, since touch never sees the
// tooltip with the full reason.
function ImagesIcon({ state = "available" }: { state?: ImagesIconState }) {
  const Icon =
    state === "empty" ? ImageOff : state === "processing" ? LoaderCircle : Images;
  return (
    <div
      className={cn(
        "flex h-full items-center justify-center",
        state === "available"
          ? "bg-brand/5 text-brand"
          : "text-muted-foreground/60",
      )}
    >
      <Icon
        className={cn(
          state === "processing" ? "h-7 w-7 motion-safe:animate-spin" : "h-9 w-9",
        )}
        strokeWidth={1.5}
        aria-hidden="true"
      />
    </div>
  );
}

function KitLogo({
  path,
  state,
}: {
  path: string;
  state: ImagesIconState;
}) {
  const { url, error } = useFirebaseUrl(path);
  // Same surface rule as the Brand Kit previews: dark behind a white logo.
  const tone = useLogoTone(url);
  if (error) return <ImagesIcon state={state} />;
  const Badge =
    state === "empty" ? ImageOff : state === "processing" ? LoaderCircle : null;
  return (
    <div
      className={cn(
        "relative flex h-full w-full items-center justify-center p-4",
        logoSurfaceClass(tone),
      )}
    >
      {url && (
        <img
          src={url}
          alt=""
          draggable={false}
          className={cn(
            "max-h-full max-w-full object-contain",
            Badge && "opacity-40 grayscale",
          )}
        />
      )}
      {/* Unavailable: the kit is recognisable, the badge says why it can't
          be picked. Top-right stays free for the selected check. */}
      {Badge && (
        <span className="absolute bottom-1.5 right-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-background text-muted-foreground shadow-sm ring-1 ring-border">
          <Badge
            className={cn(
              "h-3.5 w-3.5",
              state === "processing" && "motion-safe:animate-spin",
            )}
            aria-hidden="true"
          />
        </span>
      )}
    </div>
  );
}

/**
 * Up to four of the kit's ready photos, in upload order, filling the tile
 * evenly: a 2×2, two columns, or one. Thumbnails only — an original can be
 * 4096px, far too heavy for a tile.
 */
function kitPhotoPaths(brandKit?: BrandKit): string[] {
  const paths = (brandKit?.brandAssets || [])
    .filter(
      (asset) =>
        asset.type === "image" &&
        asset.processingStatus === "ready" &&
        asset.storagePath &&
        asset.thumbnailPath,
    )
    .map((asset) => asset.thumbnailPath!);
  return paths.slice(0, paths.length >= 4 ? 4 : paths.length >= 2 ? 2 : 1);
}

/**
 * The Link & Brand Kit tile previews the kit's photos, the way Auto
 * previews its mix. The logo (else a generic icon) holds the tile until every
 * photo has loaded, then the grid fades in at once, so it never fills in
 * piecemeal. Disabled marks the logo or icon as empty or processing.
 */
function BrandKitPreview({
  photoPaths,
  logoPath,
  iconState,
}: {
  photoPaths: string[];
  logoPath?: string;
  iconState: ImagesIconState;
}) {
  const photoUrls = useLoadedImageUrls(photoPaths);
  return (
    <div className="relative h-full w-full">
      {logoPath ? (
        <KitLogo path={logoPath} state={iconState} />
      ) : (
        <ImagesIcon state={iconState} />
      )}
      {photoPaths.length > 0 && (
        <div
          className={cn(
            "absolute inset-0 grid gap-px bg-border transition-opacity duration-300 ease-out motion-reduce:transition-none",
            photoPaths.length === 4
              ? "grid-cols-2 grid-rows-2"
              : photoPaths.length === 2
                ? "grid-cols-2"
                : "grid-cols-1",
            photoUrls ? "opacity-100" : "opacity-0",
          )}
        >
          {photoUrls?.map((url) => <SampleTile key={url} src={url} />)}
        </div>
      )}
    </div>
  );
}

export function VisualStyleSelector({
  selectedStyleId,
  onChange,
  brandKit,
  sourceMayHaveImages = false,
}: VisualStyleSelectorProps) {
  const disabledReason = brandKitImagesDisabledReason(
    brandKit,
    sourceMayHaveImages,
  );
  const disabledIconState: ImagesIconState = brandKitImagesProcessing(
    brandKit,
    sourceMayHaveImages,
  )
    ? "processing"
    : "empty";
  const disabledReasonId = useId();
  const selectedOption = VISUAL_STYLE_OPTIONS.find(
    (option) => option.id === selectedStyleId,
  );

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-3">
        <div className="flex items-baseline justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Image Style</p>
            <p className="mt-0.5 hidden max-w-[42rem] text-xs text-muted-foreground sm:block">
              Choose the look or source of images for your posts.
            </p>
          </div>
          {selectedOption && (
            <span className="hidden shrink-0 text-xs font-medium text-muted-foreground sm:inline">
              {selectedOption.label}
            </span>
          )}
        </div>

        <div className="relative">
          <div className="flex gap-2.5 overflow-x-auto scroll-smooth snap-x pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {VISUAL_STYLE_OPTIONS.map((option) => {
              const selected = selectedStyleId === option.id;
              const isAuto = option.id === "auto";
              const isBrandKitOnly = option.id === "brandkit-only";
              const disabled = isBrandKitOnly && !!disabledReason;
              return (
                <Tooltip key={option.id}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-pressed={selected}
                      aria-disabled={disabled}
                      aria-describedby={disabled ? disabledReasonId : undefined}
                      onClick={() => {
                        if (!disabled) onChange(option.id);
                      }}
                      className={cn(
                        "group relative flex-1 snap-start min-w-[120px] overflow-hidden rounded-lg border-2 bg-background p-1.5 text-left transition-[border-color,box-shadow,transform] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-safe:active:scale-[0.98]",
                        disabled && "cursor-not-allowed",
                        selected
                          ? "border-brand"
                          : disabled
                            ? "border-border"
                            : "border-border hover:border-foreground/25 hover:shadow-sm",
                      )}
                    >
                      <div className="relative aspect-[4/3] overflow-hidden rounded-md bg-muted ring-1 ring-inset ring-foreground/5">
                        {isBrandKitOnly ? (
                          <BrandKitPreview
                            photoPaths={disabled ? [] : kitPhotoPaths(brandKit)}
                            logoPath={brandKit?.logoPath}
                            iconState={disabled ? disabledIconState : "available"}
                          />
                        ) : isAuto ? (
                          <div className="grid h-full w-full grid-cols-2 grid-rows-2 gap-px bg-border">
                            {AUTO_MIX.map((id) => (
                              <SampleTile key={id} src={styleSample(id, 1)} />
                            ))}
                          </div>
                        ) : (
                          <SampleTile src={styleSample(option.id, 1)} />
                        )}
                        <span
                          className={cn(
                            "absolute right-1.5 top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand text-brand-foreground shadow-sm transition-all duration-200 ease-out motion-reduce:transition-none",
                            selected
                              ? "scale-100 opacity-100"
                              : "scale-75 opacity-0",
                          )}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </span>
                      </div>
                      <span
                        className={cn(
                          "mt-1.5 block truncate px-0.5 text-xs font-medium",
                          disabled
                            ? "text-muted-foreground"
                            : selected
                              ? "text-foreground"
                              : "text-foreground/80",
                        )}
                      >
                        {option.label}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipPortal>
                    <TooltipContent
                      sideOffset={8}
                      collisionPadding={16}
                      className="w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl p-0 text-left"
                    >
                      {isBrandKitOnly ? null : isAuto ? (
                        <div className="grid grid-cols-4 gap-1.5 px-4 pt-4">
                          {AUTO_MIX.map((id) => (
                            <div
                              key={id}
                              className="aspect-square overflow-hidden rounded bg-muted ring-1 ring-inset ring-foreground/5"
                            >
                              <SampleTile src={styleSample(id, 1)} />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-1.5 px-4 pt-4">
                          {Array.from({ length: SAMPLE_COUNT }, (_, i) => (
                            <div
                              key={i}
                              className="aspect-[4/3] overflow-hidden rounded bg-muted ring-1 ring-inset ring-foreground/5"
                            >
                              <SampleTile src={styleSample(option.id, i + 1)} />
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="space-y-1.5 px-4 py-3.5">
                        <p className="text-sm font-medium leading-5 text-foreground">
                          {option.description}
                        </p>
                        <p className="text-xs leading-5 text-muted-foreground">
                          {disabled ? disabledReason : option.brandKitBehavior}
                        </p>
                      </div>
                    </TooltipContent>
                  </TooltipPortal>
                </Tooltip>
              );
            })}
          </div>
          {/* Soft right-edge fade hints there's more to scroll. Only the
              narrow (mobile) layout overflows, so hide it once the row fits. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-background to-transparent sm:hidden"
          />
        </div>

        {disabledReason && (
          // Read by assistive tech; sighted users get the tile's icon and
          // the hover tooltip.
          <p id={disabledReasonId} className="sr-only">
            Link &amp; Brand Kit: {disabledReason}
          </p>
        )}

        {/* Touch-only one-line summary of the current choice. Desktop relies on
            the hover tooltip; on touch that never opens. */}
        {selectedOption && (
          <p className="text-xs font-medium text-foreground sm:hidden">
            {selectedOption.description}
          </p>
        )}
      </div>
    </TooltipProvider>
  );
}
